const MAX_MESSAGE_LENGTH = 450;

const PRODUCTION_CHAT_URL = 'https://chat.onthesnow.com/';

/**
 * The page the visitor launched SkiGPT from, so the chat can open with
 * that resort or region already in context. Values are the resort /
 * region `uuid`.
 */
export interface AiAssistantContext {
  resortId?: string;
  regionId?: string;
}

function sanitizeUserMessage(message: string): string {
  let sanitized = message.trim();
  sanitized = sanitized.replace(/\n{3,}/g, '\n\n');
  return sanitized;
}

function truncateMessage(message: string, maxLength: number): string {
  if (message.length <= maxLength) {
    return message;
  }

  const truncated = message.slice(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');

  if (lastSpaceIndex > maxLength * 0.8) {
    return truncated.slice(0, lastSpaceIndex);
  }

  return truncated;
}

function buildChatUrl(sanitizedMessage: string, context: AiAssistantContext = {}): string | null {
  const domain = PRODUCTION_CHAT_URL;

  if (!domain) {
    return null;
  }

  const params = [`userQuery=${encodeURIComponent(sanitizedMessage)}`];

  if (context.resortId) {
    params.push(`resortId=${encodeURIComponent(context.resortId)}`);
  }

  if (context.regionId) {
    params.push(`regionId=${encodeURIComponent(context.regionId)}`);
  }

  return `${domain}?${params.join('&')}`;
}

function openUrlSafely(url: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

  if (newWindow) {
    newWindow.opener = null;
  }
}

export function sendMessageToAiAssistant(userMessage: string, context?: AiAssistantContext): void {
  if (!userMessage || typeof userMessage !== 'string') {
    return;
  }

  const sanitized = sanitizeUserMessage(userMessage);

  if (!sanitized) {
    return;
  }

  const truncated = truncateMessage(sanitized, MAX_MESSAGE_LENGTH);
  const chatUrl = buildChatUrl(truncated, context);

  if (!chatUrl) {
    return;
  }

  openUrlSafely(chatUrl);
}
